import { captureException, captureMessage, flush } from '@sentry/nextjs'

import { isNextWebpackRuntimeModuleCallError } from '@/lib/observability/ignored-browser-errors'

const UNKNOWN_DEPLOYMENT = 'unknown-deployment'
const SERVICE_WORKER_SCOPE = '/'
const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 2000
const RECOVERY_TELEMETRY_FLUSH_TIMEOUT_MS = 500

export const CHUNK_LOAD_RECOVERY_EVENT = 'trellis:chunk-load-recovery-required'

const CHUNK_LOAD_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load chunk/i,
]

interface ChunkLoadRecoveryOptions {
  error: unknown
}

export type ChunkLoadRecoveryOutcome = 'ignored' | 'recovery-required'

type ChunkLoadRecoverySource =
  | 'react-error-boundary'
  | 'window-error'
  | 'unhandled-rejection'
  | 'early-script-error'
  | 'guest-portal-response'

declare global {
  interface Window {
    __trellisChunkLoadRecoveryInstalled?: boolean
    __trellisChunkLoadRecoveryPending?: boolean
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`
  }
  if (typeof error === 'string') {
    return error
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return typeof error.message === 'string' ? error.message : ''
  }
  return ''
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }
  return new Error(errorMessage(error) || 'Browser chunk load failed')
}

function currentWorkspaceId(): string {
  if (typeof window === 'undefined') {
    return 'unknown'
  }
  const [workspaceId] = window.location.pathname.split('/').filter(Boolean)
  return workspaceId || 'unknown'
}

function currentDeploymentId(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  return document.documentElement.dataset.dplId
}

function captureRecovery(
  error: unknown,
  source: ChunkLoadRecoverySource,
  outcome: Exclude<ChunkLoadRecoveryOutcome, 'ignored'>
): void {
  captureException(normalizeError(error), {
    tags: {
      area: 'chunk-load-recovery',
      endpoint: source,
      workspaceId: currentWorkspaceId(),
      recoveryOutcome: outcome,
    },
    extra: {
      deploymentId: currentDeploymentId() ?? UNKNOWN_DEPLOYMENT,
    },
  })
}

export function isChunkLoadError(error: unknown): boolean {
  const message = errorMessage(error)
  return (
    (message.length > 0 && CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))) ||
    isNextWebpackRuntimeModuleCallError(error)
  )
}

export function recoverFromChunkLoadError({
  error,
}: ChunkLoadRecoveryOptions): ChunkLoadRecoveryOutcome {
  if (!isChunkLoadError(error)) {
    return 'ignored'
  }
  return 'recovery-required'
}

export function recoverBrowserChunkLoadError(
  error: unknown,
  source: ChunkLoadRecoverySource
): ChunkLoadRecoveryOutcome {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) {
    return 'ignored'
  }

  const outcome = recoverFromChunkLoadError({ error })

  if (outcome === 'recovery-required') {
    requestBrowserVersionRecovery(error, source)
  }
  return outcome
}

/** Request the existing update action for a confirmed client compatibility failure. */
export function requestBrowserVersionRecovery(
  error: unknown,
  source: ChunkLoadRecoverySource
): void {
  if (typeof window === 'undefined') {
    return
  }

  window.__trellisChunkLoadRecoveryPending = true
  captureRecovery(error, source, 'recovery-required')
  window.dispatchEvent(new Event(CHUNK_LOAD_RECOVERY_EVENT))
}

export function isChunkLoadRecoveryPending(): boolean {
  return typeof window !== 'undefined' && window.__trellisChunkLoadRecoveryPending === true
}

export function installChunkLoadRecovery(): void {
  if (typeof window === 'undefined' || window.__trellisChunkLoadRecoveryInstalled) {
    return
  }
  window.__trellisChunkLoadRecoveryInstalled = true
  window.__trellisReportEarlyChunkFailure = (source) => {
    recoverBrowserChunkLoadError(new Error(`Failed to load chunk ${source}`), 'early-script-error')
  }
  for (const source of window.__trellisEarlyChunkFailures ?? []) {
    window.__trellisReportEarlyChunkFailure(source)
  }

  window.addEventListener(
    'error',
    (event) => {
      if (
        recoverBrowserChunkLoadError(event.error ?? event.message, 'window-error') ===
        'recovery-required'
      ) {
        event.preventDefault()
      }
    },
    true
  )
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (
        recoverBrowserChunkLoadError(event.reason, 'unhandled-rejection') === 'recovery-required'
      ) {
        event.preventDefault()
      }
    },
    true
  )
}

async function reloadAfterRecoveryTelemetry(): Promise<void> {
  captureMessage('User initiated app version recovery', {
    level: 'info',
    tags: {
      area: 'chunk-load-recovery',
      endpoint: 'manual-recovery',
      workspaceId: currentWorkspaceId(),
      recoveryOutcome: 'user-reload',
    },
    extra: {
      deploymentId: currentDeploymentId() ?? UNKNOWN_DEPLOYMENT,
    },
  })
  await flush(RECOVERY_TELEMETRY_FLUSH_TIMEOUT_MS).catch(() => false)
  window.location.reload()
}

export async function activateWaitingServiceWorkerAndReload(): Promise<void> {
  if (typeof window === 'undefined') {
    return
  }
  if (!('serviceWorker' in navigator)) {
    await reloadAfterRecoveryTelemetry()
    return
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE)
    if (!registration?.waiting) {
      await reloadAfterRecoveryTelemetry()
      return
    }

    let reloaded = false
    const reloadOnce = () => {
      if (reloaded) {
        return
      }
      reloaded = true
      reloadAfterRecoveryTelemetry()
    }
    const fallbackId = window.setTimeout(reloadOnce, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS)
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.clearTimeout(fallbackId)
        reloadOnce()
      },
      { once: true }
    )
    registration.waiting.postMessage({ type: 'SKIP_WAITING' })
  } catch (error) {
    captureException(normalizeError(error), {
      tags: {
        area: 'chunk-load-recovery',
        endpoint: 'activate-waiting-service-worker',
        workspaceId: currentWorkspaceId(),
      },
    })
    await reloadAfterRecoveryTelemetry()
  }
}
